// #ux-redesign-start: Input + Textarea primitives (Paso 2)
// Inputs del DS con label encima, helper text y error state visible.
// Reemplaza el patrón actual de Reactstrap Input + react-hook-form ad-hoc.
//
// PORTABILIDAD: trasplantable a Web-Patients sin cambios.
//
// USO con react-hook-form (compatible con v5/v6):
//   const { register, errors } = useForm();
//   <Input
//     name="email"
//     label="E-Mail"
//     placeholder="name@klinik.de"
//     innerRef={register({ required: 'Pflichtfeld' })}
//     error={errors.email?.message}
//     helper="Ihre Anmelde-E-Mail"
//   />
//
// USO controlado:
//   <Input label="Suche" value={q} onChange={(e) => setQ(e.target.value)} />
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import PropTypes from 'prop-types';

let inputIdCounter = 0;
const nextId = () => `ds-input-${++inputIdCounter}`;

export const Input = React.forwardRef(({
  id,
  name,
  label,
  helper,
  error,
  hint,
  type = 'text',
  size = 'md',
  disabled = false,
  required = false,
  className = '',
  innerRef,
  iconLeft,
  iconRight,
  ...rest
}, ref) => {
  const fieldId = React.useMemo(() => id || nextId(), [id]);
  const helperId = helper || error ? `${fieldId}-helper` : undefined;
  const hasError = Boolean(error);

  const wrapperClasses = [
    'ds-field',
    `ds-field--${size}`,
    hasError ? 'ds-field--error' : '',
    disabled ? 'ds-field--disabled' : '',
    iconLeft ? 'ds-field--has-icon-left' : '',
    iconRight ? 'ds-field--has-icon-right' : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={wrapperClasses}>
      {label && (
        <label htmlFor={fieldId} className="ds-field__label">
          {label}
          {required && <span className="ds-field__required" aria-hidden="true"> *</span>}
          {hint && <span className="ds-field__hint">{hint}</span>}
        </label>
      )}
      <div className="ds-field__control">
        {iconLeft && <span className="ds-field__icon ds-field__icon--left">{iconLeft}</span>}
        <input
          id={fieldId}
          name={name}
          type={type}
          ref={innerRef || ref}
          disabled={disabled}
          aria-invalid={hasError || undefined}
          aria-describedby={helperId}
          className="ds-field__input"
          {...rest}
        />
        {iconRight && <span className="ds-field__icon ds-field__icon--right">{iconRight}</span>}
      </div>
      {(error || helper) && (
        <div id={helperId} className={`ds-field__helper ${hasError ? 'ds-field__helper--error' : ''}`}>
          {error || helper}
        </div>
      )}
    </div>
  );
});

Input.displayName = 'Input';

Input.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.node,
  helper: PropTypes.node,
  error: PropTypes.node,
  hint: PropTypes.node,
  type: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  className: PropTypes.string,
  innerRef: PropTypes.any,
  iconLeft: PropTypes.node,
  iconRight: PropTypes.node,
};

export const Textarea = React.forwardRef(({
  id,
  name,
  label,
  helper,
  error,
  hint,
  rows = 4,
  size = 'md',
  disabled = false,
  required = false,
  className = '',
  innerRef,
  ...rest
}, ref) => {
  const fieldId = React.useMemo(() => id || nextId(), [id]);
  const helperId = helper || error ? `${fieldId}-helper` : undefined;
  const hasError = Boolean(error);

  const wrapperClasses = [
    'ds-field',
    `ds-field--${size}`,
    hasError ? 'ds-field--error' : '',
    disabled ? 'ds-field--disabled' : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={wrapperClasses}>
      {label && (
        <label htmlFor={fieldId} className="ds-field__label">
          {label}
          {required && <span className="ds-field__required" aria-hidden="true"> *</span>}
          {hint && <span className="ds-field__hint">{hint}</span>}
        </label>
      )}
      <div className="ds-field__control">
        <textarea
          id={fieldId}
          name={name}
          rows={rows}
          ref={innerRef || ref}
          disabled={disabled}
          aria-invalid={hasError || undefined}
          aria-describedby={helperId}
          className="ds-field__input ds-field__input--textarea"
          {...rest}
        />
      </div>
      {(error || helper) && (
        <div id={helperId} className={`ds-field__helper ${hasError ? 'ds-field__helper--error' : ''}`}>
          {error || helper}
        </div>
      )}
    </div>
  );
});

Textarea.displayName = 'Textarea';

Textarea.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.node,
  helper: PropTypes.node,
  error: PropTypes.node,
  hint: PropTypes.node,
  rows: PropTypes.number,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  className: PropTypes.string,
  innerRef: PropTypes.any,
};

export default Input;
// #ux-redesign-end
